import { useEffect, useRef } from 'react';

import { useAiContextStore } from './AiContextProvider';
import { createAiContextDataSnapshot } from './normalize-ai-context-data';
import type { AiContextRegistration } from './types';

type RegisteredSnapshot = Pick<AiContextRegistration, 'category' | 'key' | 'priority'> & {
    readonly serializedData: string;
};

export const useRegisterAiContext = (entry: AiContextRegistration): void => {
    const store = useAiContextStore();
    const registeredSnapshotRef = useRef<RegisteredSnapshot | null>(null);

    useEffect(() => {
        if (store === null) {
            return;
        }

        const dataSnapshot = createAiContextDataSnapshot(entry.data);
        const previousSnapshot = registeredSnapshotRef.current;

        if (dataSnapshot === null) {
            if (previousSnapshot !== null) {
                store.getState().unregister(previousSnapshot.key);
                registeredSnapshotRef.current = null;
            }

            return;
        }

        const nextSnapshot = {
            category: entry.category,
            key: entry.key,
            priority: entry.priority,
            serializedData: dataSnapshot.serializedData,
        } satisfies RegisteredSnapshot;

        const isUnchanged =
            previousSnapshot?.category === nextSnapshot.category &&
            previousSnapshot.key === nextSnapshot.key &&
            previousSnapshot.priority === nextSnapshot.priority &&
            previousSnapshot.serializedData === nextSnapshot.serializedData;

        if (isUnchanged) {
            return;
        }

        if (previousSnapshot !== null && previousSnapshot.key !== nextSnapshot.key) {
            store.getState().unregister(previousSnapshot.key);
        }

        store.getState().register({
            category: entry.category,
            data: dataSnapshot.normalizedData,
            key: entry.key,
            priority: entry.priority,
        });

        registeredSnapshotRef.current = nextSnapshot;
    });

    useEffect(() => {
        return () => {
            if (store === null) {
                return;
            }

            const key = registeredSnapshotRef.current?.key;

            if (key === undefined) {
                return;
            }

            store.getState().unregister(key);
            registeredSnapshotRef.current = null;
        };
    }, [store]);
};
